import { Box, Text, Flex, Badge, useBreakpointValue, Icon } from '@chakra-ui/react'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { MdCalendarToday, MdAccessTime, MdMeetingRoom, MdEdit, MdVpnKey } from 'react-icons/md'
import { useRooms } from '../../hooks/coreApi/useRooms'
import { useReservation } from './ReservationContext'

type ReservationInfoPanelProps = {
  showTitle?: boolean
  showPassword?: boolean
}

const ReservationInfoPanel: React.FC<ReservationInfoPanelProps> = ({ showTitle = false, showPassword = false }) => {
  const { state } = useReservation()
  const roomsQuery = useRooms({ isPasswordProtected: true })
  const isMobile = useBreakpointValue({ base: true, md: false })
  
  // 選択した部屋の情報を取得
  const selectedRoom = useMemo(() => {
    const rooms = roomsQuery.data?.rooms ?? []
    return rooms.find(room => room.id === state.roomId)
  }, [roomsQuery.data, state.roomId])
  
  // 日付をフォーマット
  const formattedDate = useMemo(() => {
    if (!state.date) return '日付未選択'
    return dayjs(state.date).format('YYYY年MM月DD日')
  }, [state.date])

  // 時間の長さを計算
  const duration = useMemo(() => {
    if (!state.startTime || !state.endTime) return 0
    
    const [startHour, startMinute] = state.startTime.split(':').map(Number)
    const [endHour, endMinute] = state.endTime.split(':').map(Number)
    
    const startTotalMinutes = startHour * 60 + startMinute
    const endTotalMinutes = endHour * 60 + endMinute
    
    return endTotalMinutes - startTotalMinutes
  }, [state.startTime, state.endTime])

  return (
    <Box 
      p={4} 
      borderRadius="md" 
      bg="gray.50"
      boxShadow="sm"
      w="100%"
      maxW="100%"
    >
      <Text fontSize="16px" fontWeight="600" mb={3} color="gray.700">
        予約情報
      </Text>
      <Flex direction="column" gap={2} w="100%">
        <Flex direction={isMobile ? "column" : "row"} justify={isMobile ? "flex-start" : "space-between"} align={isMobile ? "flex-start" : "flex-start"} w="100%">
          <Flex alignItems="center" gap="4px" minW={isMobile ? "auto" : "60px"} mb={isMobile ? 1 : 0} flexShrink={0}>
            <Icon as={MdCalendarToday} boxSize="16px" color="gray.600" />
            <Text fontSize="14px" color="gray.600">日付:</Text>
          </Flex>
          <Text fontSize="14px" fontWeight="600" wordBreak="break-word" whiteSpace="normal" flex="1">{formattedDate}</Text>
        </Flex>
        <Flex direction={isMobile ? "column" : "row"} justify={isMobile ? "flex-start" : "space-between"} align={isMobile ? "flex-start" : "flex-start"} w="100%">
          <Flex alignItems="center" gap="4px" minW={isMobile ? "auto" : "60px"} mb={isMobile ? 1 : 0} flexShrink={0}>
            <Icon as={MdAccessTime} boxSize="16px" color="gray.600" />
            <Text fontSize="14px" color="gray.600">時間:</Text>
          </Flex>
          <Text fontSize="14px" fontWeight="600" wordBreak="break-word" whiteSpace="normal" flex="1">
            {state.startTime ?? '未選択'} 〜 {state.endTime ?? '未選択'}
            {state.startTime && state.endTime && duration > 0 && ` (${duration}分間)`}
          </Text>
        </Flex>
        <Flex direction={isMobile ? "column" : "row"} justify={isMobile ? "flex-start" : "space-between"} align={isMobile ? "flex-start" : "flex-start"} w="100%">
          <Flex alignItems="center" gap="4px" minW={isMobile ? "auto" : "60px"} mb={isMobile ? 1 : 0} flexShrink={0}>
            <Icon as={MdMeetingRoom} boxSize="16px" color="gray.600" />
            <Text fontSize="14px" color="gray.600">会議室:</Text>
          </Flex>
          <Badge colorScheme="green" fontSize="14px" maxW="100%" wordBreak="break-word" whiteSpace="normal" flex="1">
            {selectedRoom?.name ?? '未選択'} 部屋
          </Badge>
        </Flex>
        {showTitle && (
          <Flex direction={isMobile ? "column" : "row"} justify={isMobile ? "flex-start" : "space-between"} align={isMobile ? "flex-start" : "flex-start"} w="100%">
            <Flex alignItems="center" gap="4px" minW={isMobile ? "auto" : "60px"} mb={isMobile ? 1 : 0} flexShrink={0}>
              <Icon as={MdEdit} boxSize="16px" color="gray.600" />
              <Text fontSize="14px" color="gray.600">会議名:</Text>
            </Flex>
            <Text fontSize="14px" fontWeight="600" maxW="100%" wordBreak="break-word" whiteSpace="normal" flex="1">
              {state.title || '未入力'}
            </Text>
          </Flex>
        )}
        {showPassword && (
          <Flex direction={isMobile ? "column" : "row"} justify={isMobile ? "flex-start" : "space-between"} align={isMobile ? "flex-start" : "flex-start"} w="100%">
            <Flex alignItems="center" gap="4px" minW={isMobile ? "auto" : "60px"} mb={isMobile ? 1 : 0} flexShrink={0}>
              <Icon as={MdVpnKey} boxSize="16px" color="gray.600" />
              <Text fontSize="14px" color="gray.600">パスワード:</Text>
            </Flex>
            <Text fontSize="14px" fontWeight="600" maxW="100%" wordBreak="break-word" whiteSpace="normal" flex="1">
              {state.password || '設定なし'}
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  )
}

export default ReservationInfoPanel 